import { api as baseApi } from "./emptyApi.ts";
import {
  getAuthToken,
  handleUnauthorized,
} from "../utils/authSession.js";

const streamBodies = new Map<string, FormData>();

export function putStreamBody(key: string, body: FormData) {
  streamBodies.set(key, body);
}

function takeStreamBody(key: string): FormData | undefined {
  const body = streamBodies.get(key);
  streamBodies.delete(key);
  return body;
}

type StreamState = {
  requestKey: string;
  text: string;
  done: boolean;
  error?: string;
  conversationId?: string;
  statusMessage?: string;        
  statusCode?: string;
  statuses?: string[];
  summary?: string;
  products?: any[];
  metadata?: any;
};

type StreamArg = {
  key: string; // unique per request (timestamp/uuid)
  url: string; // full endpoint url
};

function parseLineToEvent(line: string): any | null {
  const trimmed = (line || "").trim();
  if (!trimmed) return null;

  let jsonStr: string | null = null;
  if (trimmed.startsWith("data:")) jsonStr = trimmed.slice(5).trim();
  else if (trimmed.startsWith("{") && trimmed.endsWith("}")) jsonStr = trimmed;

  if (!jsonStr) return null;

  try {
    return JSON.parse(jsonStr);
  } catch {
    return null;
  }
}

export const streamingApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    chatStream: build.query<StreamState, StreamArg>({
      serializeQueryArgs: ({ queryArgs }) => queryArgs.key,

      queryFn: (arg) => ({
        data: {
          requestKey: arg.key,
          text: "",
          done: false,
          statuses: [],
        },
      }),

      async onCacheEntryAdded(
        arg,
        { updateCachedData, cacheDataLoaded, cacheEntryRemoved }
      ) {
        await cacheDataLoaded;

        const controller = new AbortController();

        try {
          const body = takeStreamBody(arg.key);
          if (!body) {
            updateCachedData((d) => {
              d.done = true;
              d.error = "Missing request body for stream (FormData not found).";
            });
            return;
          }
          const isPublicStream =
            arg.url.includes("/public-") ||
            arg.url.includes("/public/");

          const token = isPublicStream
            ? null
            : getAuthToken();
          const headers: Record<string, string> = {};

          if (token) {
            headers.Authorization = `Token ${token}`;
          }
          const res = await fetch(arg.url, {
            method: "POST",
            body,
            headers,
            signal: controller.signal,
          });
          if (res.status === 401 && handleUnauthorized(token)) {
            return;
          }

          if (!res.ok) {
            const errorText = await res.text().catch(() => "");
            updateCachedData((draft) => {
              draft.done = true;
              draft.error = errorText || `HTTP ${res.status}`;
            });
            return;
          }

          const convHeader =
            res.headers.get("X-Conversation-Id") ||
            res.headers.get("x-conversation-id") ||
            undefined;

          if (convHeader) {
            updateCachedData((d) => {
              d.conversationId = convHeader;
            });
          }

          const reader = res.body?.getReader();
          if (!reader) {
            updateCachedData((d) => {
              d.done = true;
              d.error = "Streaming not supported (no response body)";
            });
            return;
          }

          const decoder = new TextDecoder("utf-8");
          let buffer = "";
          let doneFullText: string | null = null;

          while (true) {
            const { value, done } = await reader.read();
            if (done) break;

            buffer += decoder.decode(value, { stream: true });

            const lines = buffer.split("\n");
            buffer = lines.pop() || "";

            for (const line of lines) {
              const evt = parseLineToEvent(line);
              if (!evt) continue;
              if (evt.type === "metadata") {
                updateCachedData((d) => {
                  d.metadata = evt;
                });
                continue;
              }
              if (evt.type === "status" && typeof evt.message === "string") {
                const msg = evt.message.trim();
                updateCachedData((d) => {
                  d.statusMessage = msg;
                  if (typeof evt.status === "string") d.statusCode = evt.status;
                  if (!Array.isArray(d.statuses)) d.statuses = [];
                  if (msg && !d.statuses.includes(msg)) d.statuses.push(msg);
                });
                continue;
              }

              if (evt.type === "content" && typeof evt.chunk === "string") {
                updateCachedData((d) => {
                  d.text += evt.chunk;
                });
              } else if (evt.type === "done") {
                if (typeof evt.full_text === "string" && evt.full_text.trim()) {
                  doneFullText = evt.full_text;
                }
                updateCachedData((d) => {
                  d.done = true;
                  if (doneFullText) d.text = doneFullText;
                  if (!d.conversationId && evt.conversation_id) {
                    d.conversationId = String(evt.conversation_id);
                  }
                  if (typeof evt.summary === "string") {
                    d.summary = evt.summary;
                  }
                  if (Array.isArray(evt.products)) {
                    d.products = evt.products;
                  }
                });

                try {
                  controller.abort();
                } catch {}
                return;
              } else if (evt.type === "error") {
                updateCachedData((d) => {
                  d.done = true;
                  d.error = evt.detail || "Stream error";
                });
                try {
                  controller.abort();
                } catch {}
                return;
              }
            }
          }

          updateCachedData((d) => {
            d.done = true;
          });
        } catch (e: any) {
          if (e?.name !== "AbortError") {
            updateCachedData((d) => {
              d.done = true;
              d.error = e?.message || "Stream failed";
            });
          }
        } finally {
          await cacheEntryRemoved;
          try {
            controller.abort();
          } catch {}
          try {
            streamBodies.delete(arg.key);
          } catch {}
        }
      },
    }),
  }),
  overrideExisting: false,
});

export const { useLazyChatStreamQuery } = streamingApi;
